import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '@/shared/config/firebase';
import {
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  collection,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';

interface PresenceData {
  userId: string;
  userName: string;
  userType: 'customer' | 'admin';
  lastSeen: Timestamp | null;
  typingTimestamp: Timestamp | null;
}

interface ComputedPresence {
  userId: string;
  userName: string;
  userType: 'customer' | 'admin';
  isOnline: boolean;
  isTyping: boolean;
  lastSeen: Date;
}

// Constantes pour les seuils temporels
const ONLINE_THRESHOLD_MS = 70000; // 70 secondes (marge pour le heartbeat)
const TYPING_THRESHOLD_MS = 3000; // 3 secondes

/**
 * Hook pour gérer la présence temps réel dans un chat SAV
 * - Pas de polling
 * - Calcul dynamique de l'état online/offline
 * - Auto-expiration de l'indicateur "en train d'écrire"
 */
export const useChatPresence = (
  chatId: string | null,
  userId: string | null,
  userName: string,
  userType: 'customer' | 'admin'
) => {
  const [otherUserPresence, setOtherUserPresence] = useState<ComputedPresence | null>(null);
  const typingDebounceRef = useRef<NodeJS.Timeout>();
  const presenceDocRef = useRef<string | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout>();

  // Calculer si l'utilisateur est en ligne et en train d'écrire
  const computePresence = useCallback((data: PresenceData): ComputedPresence => {
    const now = Date.now();
    
    // Calculer isOnline basé sur lastSeen
    let isOnline = false;
    if (data.lastSeen) {
      const lastSeenMs = data.lastSeen.toMillis();
      isOnline = (now - lastSeenMs) < ONLINE_THRESHOLD_MS;
    }

    // Calculer isTyping basé sur typingTimestamp
    let isTyping = false;
    if (data.typingTimestamp && isOnline) {
      const typingMs = data.typingTimestamp.toMillis();
      isTyping = (now - typingMs) < TYPING_THRESHOLD_MS;
    }

    return {
      userId: data.userId,
      userName: data.userName,
      userType: data.userType,
      isOnline,
      isTyping,
      lastSeen: data.lastSeen?.toDate() || new Date(),
    };
  }, []);

  // Setup presence for current user
  useEffect(() => {
    if (!chatId || !userId) return;

    const presencePath = `sav_chats/${chatId}/presence/${userId}`;
    presenceDocRef.current = presencePath;

    // Initialiser la présence
    const initializePresence = async () => {
      try {
        await setDoc(doc(db, presencePath), {
          userId,
          userName,
          userType,
          lastSeen: serverTimestamp(),
          typingTimestamp: null,
        });
      } catch (error) {
        console.error('Error initializing presence:', error);
      }
    };

    initializePresence();

    // Heartbeat pour maintenir "en ligne" (toutes les 45s)
    heartbeatIntervalRef.current = setInterval(() => {
      if (!document.hidden) {
        setDoc(doc(db, presencePath), {
          lastSeen: serverTimestamp(),
        }, { merge: true }).catch((err) => {
          console.error('Error updating heartbeat:', err);
        });
      }
    }, 45000);

    // Gestion de la visibilité (changement d'onglet)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab caché : mettre lastSeen mais garder le document
        setDoc(doc(db, presencePath), {
          lastSeen: serverTimestamp(),
          typingTimestamp: null,
        }, { merge: true });
      } else {
        // Tab visible : update lastSeen
        setDoc(doc(db, presencePath), {
          lastSeen: serverTimestamp(),
        }, { merge: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Marquer la dernière fois vu au démontage
      if (presenceDocRef.current) {
        setDoc(doc(db, presenceDocRef.current), {
          lastSeen: serverTimestamp(),
          typingTimestamp: null,
        }, { merge: true }).catch((err) => {
          console.error('Error cleaning up presence:', err);
        });
      }
    };
  }, [chatId, userId, userName, userType]);

  // Écouter la présence de l'autre utilisateur
  useEffect(() => {
    if (!chatId) return;

    const otherUserType = userType === 'customer' ? 'admin' : 'customer';
    const presenceCollectionPath = `sav_chats/${chatId}/presence`;

    const presenceQuery = query(
      collection(db, presenceCollectionPath),
      where('userType', '==', otherUserType)
    );

    // Stocker la dernière donnée de présence
    let lastPresenceData: PresenceData | null = null;
    let refreshInterval: NodeJS.Timeout;

    const unsubscribe = onSnapshot(
      presenceQuery,
      (snapshot) => {
        if (snapshot.empty) {
          setOtherUserPresence(null);
          if (refreshInterval) clearInterval(refreshInterval);
          lastPresenceData = null;
          return;
        }

        lastPresenceData = snapshot.docs[0].data() as PresenceData;
        
        // Calculer l'état initial
        const computed = computePresence(lastPresenceData);
        setOtherUserPresence(computed);

        // Rafraîchir le calcul toutes les secondes SEULEMENT si isTyping peut changer
        if (refreshInterval) clearInterval(refreshInterval);
        
        // Ne rafraîchir que si la personne est en train d'écrire
        if (computed.isTyping) {
          refreshInterval = setInterval(() => {
            if (lastPresenceData) {
              const recomputed = computePresence(lastPresenceData);
              
              // Ne mettre à jour l'état que si les valeurs ont changé
              setOtherUserPresence(prev => {
                if (!prev) return recomputed;
                if (prev.isOnline !== recomputed.isOnline || prev.isTyping !== recomputed.isTyping) {
                  return recomputed;
                }
                return prev; // Pas de changement, garder l'état précédent
              });
              
              // Arrêter le rafraîchissement si plus en train d'écrire
              if (!recomputed.isTyping) {
                clearInterval(refreshInterval);
              }
            }
          }, 1000);
        }
      },
      (error) => {
        console.error('Error listening to presence:', error);
      }
    );

    return () => {
      unsubscribe();
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [chatId, userType, computePresence]);

  // Set typing indicator avec debounce
  const setTyping = useCallback(
    async (isTyping: boolean) => {
      if (!chatId || !userId || !presenceDocRef.current) return;

      // Debounce : éviter d'écrire à chaque frappe
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
      }

      if (isTyping) {
        typingDebounceRef.current = setTimeout(async () => {
          try {
            await setDoc(
              doc(db, presenceDocRef.current!),
              {
                typingTimestamp: serverTimestamp(),
                lastSeen: serverTimestamp(),
              },
              { merge: true }
            );
          } catch (error) {
            console.error('Error setting typing status:', error);
          }
        }, 300); // 300ms debounce
      } else {
        // Clear typing immédiatement quand on arrête
        try {
          await setDoc(
            doc(db, presenceDocRef.current),
            {
              typingTimestamp: null,
              lastSeen: serverTimestamp(),
            },
            { merge: true }
          );
        } catch (error) {
          console.error('Error clearing typing status:', error);
        }
      }
    },
    [chatId, userId]
  );

  return {
    otherUserPresence,
    setTyping,
  };
};
